import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { isDevMode } from '@angular/core';
import { Environment } from '@core/services/common';
import { Platform } from '@ionic/angular';
import { DayHourRange, WorkingDaysAndHours } from '@models/patrimonial';
import { PolicyBase } from '@models/policy';
import { PolicyCollectiveLife } from '@models/policy-collective-life';
import { PolicyLife } from '@models/policy-life';
import { PolicySaving } from '@models/policy-saving';
import * as moment from 'moment';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

export function isValidEmail(email: string) {
  const regexp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  return regexp.test(email);
}

export function isValidMobile(mobile: string) {
  const regex = new RegExp(/^((6|7)[0-9]{8})$/);
  return regex.test(mobile);
}

export function isValidPhone(phone: string) {
  const regex = new RegExp(/^((8|9)[0-9]{8})$/);
  return regex.test(phone);
}

export function isValidPostalCode(code: string) {
  const regex = new RegExp(/(^(0[1-9]|[1-4][0-9]|5[0-2])[0-9]{3}$)/);
  return regex.test(code);
}

export function isValidNifOrCif(nif: string) {
  const DNI_REGEX = new RegExp(/^(\d{8})([A-Z])$/);
  const CIF_REGEX = new RegExp(/^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/);

  nif = nif.toUpperCase().replace(/\s/, '');

  return DNI_REGEX.test(nif) || CIF_REGEX.test(nif);
}

export function isValidNif(nif: string) {
  const DNI_REGEX = new RegExp(/^(\d{8})([A-Z])$/);

  nif = nif.toUpperCase().replace(/\s/, '');

  return DNI_REGEX.test(nif);
}
export function isValidPartialNifOrCif(nif: string) {
  const DNI_REGEX = new RegExp(/^(0?\d{1,8})([A-Z])$/);
  const CIF_REGEX = new RegExp(/^([ABCDEFGHJKLMNPQRSUVW])(\d{1,7})([0-9A-J])$/);

  nif = nif.toUpperCase().replace(/\s/, '');

  return DNI_REGEX.test(nif) || CIF_REGEX.test(nif);
}

export function isValidPartialNif(nif: string) {
  const dniRegex = new RegExp(/^(0?\d{1,8})([A-Z])$/);
  nif = nif.toUpperCase().replace(/\s/, '');
  return dniRegex.test(nif);
}

export function isValidPassword(password: string) {
  const regex = new RegExp(/^[a-zA-Z0-9]*$/g);
  return regex.test(password);
}

export function isValidAnswer(answer: string) {
  const regex = new RegExp(/^[a-zA-Z0-9\u00f1\u00d1]*$/g);
  return regex.test(answer);
}
/**
 * Completes a valid partial NIF to a full NIF with
 * @param partialNif string
 * @return full NIF
 */
export function completeNifPadding(partialNif: string) {
  try {
    const lastLetter = partialNif[partialNif.length - 1];
    const numbersSection = partialNif.slice(0, -1);
    return `${padZeros(numbersSection, 8)}${lastLetter}`;
  } catch (err) {
    return '';
  }
}

export function isValidNie(nie: string) {
  const NIE_REGEX = new RegExp(/^[XYZ]\d{7}[A-Z]$/);

  nie = nie.toUpperCase().replace(/\s/, '');

  return NIE_REGEX.test(nie);
}

export function isValidPartialNie(nie: string) {
  const NIE_REGEX = new RegExp(/^[XYZ]\d{1,7}[A-Z]$/);

  nie = nie.toUpperCase().replace(/\s/, '');

  return NIE_REGEX.test(nie);
}

/**
 * Completes a valid partial NIE to a full NIE with
 * @param partialNie string
 * @return full NIE
 */
export function completeNiePadding(partialNie: string) {
  try {
    const firstLetter = partialNie[0];
    const lastLetter = partialNie[partialNie.length - 1];
    const numbersSection = partialNie.slice(1, -1);
    return `${firstLetter}${padZeros(numbersSection, 7)}${lastLetter}`;
  } catch (err) {
    return '';
  }
}

export function isOlderThan(date: string | moment.Moment, age: number, format = 'YYYY-MM-DD') {
  return getAge(date, format) >= age;
}

export function isGreaterYearThan(date: string | moment.Moment, year: number, format = 'YYYY-MM-DD') {
  return moment(date, format).year() > year;
}

export function isFutureDate(date: string | moment.Moment, format = 'YYYY-MM-DD') {
  return moment(date, format).isAfter(moment().format(format));
}

export function isSameOrAfterCurrentDate(date: string | moment.Moment, format = 'YYYY-MM-DD') {
  return isSameOrAfterDate(date, moment().format(format), format);
}

export function isSameOrAfterDate(date1: string | moment.Moment, date2: string | moment.Moment, format = 'YYYY-MM-DD') {
  return moment(date1, format).isSameOrAfter(moment(date2, format));
}

/**
 * Set left zeros to complete padding digits or 10 digits
 * @param input string
 * @param desiredLength number of postions to pad
 * @return formated string
 */
export function padZeros(input: string, desiredLength = 10) {
  const zeros = '0'.repeat(desiredLength);
  return (zeros + input).slice(-desiredLength).toUpperCase();
}

/**
 * Get numeric age in years
 * @param birthdate string
 * @return numeric age (years)
 */
export function getAge(
  birthdate: string | moment.Moment,
  format = 'YYYY-MM-DD'
) {
  return moment().diff(moment(birthdate, format), 'years');
}

export function getDaysUntilToday(date: string | moment.Moment, format = 'YYYY-MM-DD') {
  return moment().diff(moment(date, format), 'days');
}

/**
 * Returns a random number between min (inclusive) and max (exclusive)
 */
export function getRandomNumber(min = 0, max = 1) {
  return Math.random() * (max - min) + min;
}

export function convertb64toBlob(
  b64Data,
  contentType: string,
  sliceSize?: number
) {
  if (b64Data instanceof Blob) {
    return b64Data;
  }

  contentType = contentType || '';
  sliceSize = sliceSize || 512;

  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

/**
 * New b64toBlob function
 */
export function b64UrltoBlob(base64) {
  if (base64 instanceof Blob) {
    return Promise.resolve(base64);
  }
  if (typeof base64 !== 'string') {
    throw new Error('Invalid base64');
  }
  return fetch(base64).then(res => res.blob());
}

export function convertBlobTob64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();
      let base64Data: string;
      reader.onload = _ => {
        const dataUrl = reader.result as string;
        base64Data = dataUrl.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = error => {
        reject(error);
      };
      reader.readAsDataURL(file);
    } else {
      reject();
    }
  });
}

export async function getDataURLFromFile(file: File) {
  const reader = new FileReader();
  return new Promise<string>((resolve, reject) => {
    reader.onload = _ => {
      resolve('' + reader.result);
    };
    reader.onerror = error => {
      reject(error);
    };
    if (file instanceof Blob) {
      reader.readAsDataURL(file);
    }
  });
}

export function encodeAddressForGoogle(address: string) {
  const normalizedString = address
    ? address
        .replace(/\!/g, '')
        .replace(/\%/g, '')
        .replace(/\*/g, '')
        .replace(/\'/g, '´')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
        .replace(/\;/g, '')
        .replace(/\:/g, '')
        .replace(/\@/g, '')
        .replace(/\&/g, '')
        .replace(/\=/g, '')
        .replace(/\+/g, '')
        .replace(/\$/g, '')
        .replace(/\//g, '')
        .replace(/\?/g, '')
        .replace(/\#/g, '')
        .replace(/\[/g, '')
        .replace(/\]/g, '')
    : '';
  return encodeURI(normalizedString);
}

export function formatDocumentNumber(documentNumber: string) {
  if (!documentNumber) {
    return '';
  }
  while (documentNumber.length < 10) {
    documentNumber = '0' + documentNumber;
  }
  if (documentNumber.length > 9) {
    documentNumber = documentNumber.substring(
      documentNumber.length - 9,
      documentNumber.length
    );
    return documentNumber;
  }
  return documentNumber;
}

export function safeTrim(text: string) {
  return text ? text.trim() : text;
}

export function removeWhiteSpaces(text: string): string {
  return safeTrim(text) ? safeTrim(text).replace(/\s/g, '') : text;
}

export function getQueryParamsObject(url) {
  const paramsObject = {};
  if (url && url.indexOf('?') >= 0) {
    const splitURL = url.split('?');
    if (splitURL[1]) {
      const splitParams = splitURL[1].split('&');
      splitParams.forEach(param => {
        if (param) {
          const separatorIndex = param.indexOf('=');
          if (separatorIndex < param.length - 1) {
            const paramKey = param.slice(0, separatorIndex);
            const paramValue = param.slice(separatorIndex + 1);
            paramsObject[paramKey] = paramValue;
          }
        }
      });
    }
  }
  return paramsObject;
}

/**
 * Check if documentNumber is a valid DNI, NIE, CIF or NIF
 */
export function validateDocument(documentNumber: string) {
  if (documentNumber) {
    documentNumber = documentNumber.toUpperCase();
  }
  const dniRegex = new RegExp(/^(0?\d{1,8})([A-Z])$/);
  const cifRegex = new RegExp(/^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/);
  const nieRegex = new RegExp(/^[XYZ]\d{7,8}[A-Z]$/);

  if (documentNumber == null) {
    return false;
  }
  // DNI
  if (dniRegex.test(documentNumber)) {
    documentNumber = documentNumber.padStart(9, '0');
    return validateLetter(documentNumber);
  }

  // NIE
  if (nieRegex.test(documentNumber)) {
    let nie: string;
    switch (documentNumber[0]) {
      case 'X':
        nie = '0' + documentNumber.slice(1);
        break;
      case 'Y':
        nie = '1' + documentNumber.slice(1);
        break;
      case 'Z':
        nie = '2' + documentNumber.slice(1);
        break;
      default:
        nie = documentNumber;
        break;
    }
    return validateLetter(nie);
  }

  // CIF
  if (cifRegex.test(documentNumber)) {
    // Delete first characted and last number
    const valueCif = documentNumber.substr(1, documentNumber.length - 2);

    let suma = 0;

    // Add the even numbers of the element
    for (let i = 1; i < valueCif.length; i = i + 2) {
      suma = suma + parseInt(valueCif.substr(i, 1), 10);
    }
    let suma2 = 0;
    // Add the odd numbers of the element
    for (let i = 0; i < valueCif.length; i = i + 2) {
      const result = parseInt(valueCif.substr(i, 1), 10) * 2;
      if (String(result).length === 1) {
        // one caracter
        suma2 = suma2 + result;
      } else {
        // two caracters
        suma2 =
          suma2 +
          parseInt(String(result).substr(0, 1), 10) +
          parseInt(String(result).substr(1, 1), 10);
      }
    }

    // Add the lasts adds
    suma = suma + suma2;
    let unidad = ('' + suma).substr(1, 1);
    unidad = '' + (10 - +unidad);

    const primerCaracter = documentNumber.substr(0, 1).toUpperCase();

    if (primerCaracter.match(/^[FJKNPQRSUVW]$/)) {
      // starts with .... compare the last letter
      if (
        String.fromCharCode(64 + +unidad).toUpperCase() ===
        documentNumber.substr(documentNumber.length - 1, 1).toUpperCase()
      ) {
        return true;
      }
    } else if (primerCaracter.match(/^[XYZ]$/)) {
      // validate like DNI
      let newcif;
      if (primerCaracter === 'X') {
        newcif = documentNumber.substr(1);
      }
      if (primerCaracter === 'Y') {
        newcif = '1' + documentNumber.substr(1);
      }
      if (primerCaracter === 'Z') {
        newcif = '2' + documentNumber.substr(1);
      }
      if (dniRegex.test(newcif)) {
        return validateLetter(newcif);
      }
    } else if (primerCaracter.match(/^[ABCDEFGHLM]$/)) {
      // Check if the last value matches the calculation
      if (+unidad === 10) {
        unidad = '0';
      }
      if (documentNumber.substr(documentNumber.length - 1, 1) === '' + unidad) {
        return true;
      }
    } else {
      // validate like DNI
      if (dniRegex.test(documentNumber)) {
        return validateLetter(documentNumber);
      }
    }
    return false;
  }
  return false;
}

export function validateNifNiePassport(nifNumber: string) {
  const nifRegex = new RegExp(/^([XYZxyz]{1}[0-9]{7}([a-z]|[A-Z]{1}))|(([0-9]{7,8})([a-z]|[A-Z]{1}))$/i);
  if (nifNumber == null) { return false; }
  return nifRegex.test(nifNumber);
}

export function validateCif(cifNumber: string) {
  const cifRegex = new RegExp(/^([ABCDEFGHJKLMNPQRSUVWabcdefghjklmnpqrsuvw])(\d{7})([0-9A-Ja-j])$/);
  if (cifNumber == null) { return false; }
  return cifRegex.test(cifNumber);
}

function validateLetter(doc: string) {
  const dniLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const letter = dniLetters.charAt(parseInt(doc, 10) % 23);
  return letter === doc.charAt(doc.length - 1);
}

export function getConcatenatedUserName(
  name: string,
  surname1: string,
  surname2: string
) {
  return `${name ? name + ' ' : ''}${surname1 ? surname1 + ' ' : ''}${
    surname2 ? surname2 : ''
  }`.trim();
}

export function getConcatenatedUserNameAndCapitalize(
  name: string,
  surname1: string,
  surname2: string
) {
  return `${
    name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() + ' ' : ''
  }${
    surname1
      ? surname1.charAt(0).toUpperCase() + surname1.slice(1).toLowerCase() + ' '
      : ''
  }${
    surname2
      ? surname2.charAt(0).toUpperCase() + surname2.slice(1).toLowerCase()
      : ''
  }`.trim();
}

export function applyMaxLength(
  text: string,
  maxLength: number,
  fromEnd?: boolean
) {
  if (!text || maxLength < 0) {
    return text;
  }
  if (text.length <= maxLength) {
    return text;
  }

  if (fromEnd) {
    return text.slice(text.length - maxLength);
  }

  return text.slice(0, maxLength);
}

export function makeBgOpaque() {
  const nodeList = document.querySelectorAll('._gmaps_cdv_');
  if (nodeList && nodeList.length > 0) {
    nodeList.forEach(item => {
      item.classList.remove('transparent');
    });
  }
}

export function createAnchorTag(
  file: string | Blob,
  name: string,
  open?: boolean
) {
  if (window.navigator && (window as any).navigator.msSaveOrOpenBlob) {
    // EDGE support
    (window as any).navigator.msSaveOrOpenBlob(file as Blob, name);
  } else {
    if (file instanceof Blob) {
      file = URL.createObjectURL(file);
    }
    const link = document.createElement('a');
    link.href = file;
    if (open) {
      link.target = '_blank';
    } else {
      link.download = name;
    }
    (link.attributes as any).analyticsDisable = true;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export function jsonParse(str: string) {
  try {
    return JSON.parse(str);
  } catch (e) {
    return str;
  }
}

export function jsonStringify(str: string) {
  if (typeof str === 'string') return str;
  try {
    return JSON.stringify(str);
  } catch (e) {
    return str;
  }
}

export function isEmpty(element) {
  if (Array.isArray(element)) return !element.length;

  return element === undefined || element === null;
}

export class CustomUriEncoder implements HttpParameterCodec {
  /**
   * This encoder is needed in order to format Date query params for HttpParams.
   * https://medium.com/better-programming/how-to-fix-angular-httpclient-not-escaping-url-parameters-ddce3f9b8746
   * Example:
   * 2020-02-10T12:48:20.000+01:00 -> 2020-02-10T12%3A48%3A20.000%2B01%3A00
   */
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

export function getCookie(cname) {
  const name = cname + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let c of ca) {
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

export function hasAdblock() {
  return fetch('https://static.doubleclick.net/instream/ad_status.js')
    .then(() => false)
    .catch(response => response instanceof Error || response.status === 0);
}

export const toBoolean = v => !!v;
export const negateOrNull = v => (v == null ? null : !v);
export const isBoolean = v => typeof v === 'boolean';

export function getDOMPath(event) {
  const path = [];
  let currentElem = event.target;
  while (currentElem) {
    path.push(currentElem);
    currentElem = currentElem.parentElement;
  }
  if (path.indexOf(window) === -1 && path.indexOf(document) === -1)
    path.push(document);
  if (path.indexOf(window) === -1) path.push(window);
  return path;
}

export function getReferrer() {
  return document.referrer;
}

export function getUserLanguage() {
  return navigator.language;
}

export function getUserAgent() {
  return navigator.userAgent;
}

export function limitTextLength(text: string, maxLength: number) {
  if (!text) {
    return text;
  }
  return text.substr(0, maxLength);
}

export function sortStrings(
  string1: string,
  string2: string,
  descendant = false
) {
  const a = string1 || '';
  const b = string2 || '';
  if (descendant) {
    return a > b ? -1 : a < b ? 1 : 0;
  } else {
    return a > b ? 1 : a < b ? -1 : 0;
  }
}

export function sortNumbers(
  number1: number,
  number2: number,
  descendant = false
) {
  const a = number1 || 0;
  const b = number2 || 0;
  if (descendant) {
    return a > b ? -1 : a < b ? 1 : 0;
  } else {
    return a > b ? 1 : a < b ? -1 : 0;
  }
}

export function parseObjectToKeyValueArray(obj: { [key: string]: string }) {
  return Object.entries(obj).map(([field, value]) => ({ field, value }));
}

export function trueOrFalseStringToBoolean(val: 'true' | 'false') {
  try {
    return JSON.parse(val.toLowerCase());
  } catch {
    return false;
  }
}

export function isProduction() {
  return Environment.config().envName === 'production';
}

export function isPreproduction() {
  return Environment.config().envName === 'preproduction';
}

export function isSafari() {
  return (
    /Safari/.test(navigator.userAgent) &&
    /Apple Computer/.test(navigator.vendor)
  );
}

/**
 * @description creates object from array of tuples [key, value][]
 * @example
 * [[key1, value1], [key2, value2]].reduce(fromEntriesArr, {})
 */
export function fromEntriesArr(accum, [k, v]) {
  accum[k] = v;
  return accum;
}

export function fromEntries(xs: [string | number | symbol, any][]) {
  return (Object as any).fromEntries
    ? (Object as any).fromEntries(xs)
    : xs.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

export function diffGreaterOrEqualThan(years: number, startIso, endIso) {
  if (!startIso || !endIso) return false;

  const startMoment = moment(startIso);
  const endMoment = moment(endIso);
  const validYears = endMoment.diff(startMoment, 'years');
  return validYears >= years;
}

export function iterate<T>(obj: T | T[], fn: (obj: T) => any) {
  const isArray = Array.isArray(obj);
  const objArray = isArray ? obj : [obj];
  const res = (objArray as T[]).map(i => fn(i));

  return isArray ? res : res[0];
}

export const addIsLifetimePolicy = map(
  (
    p:
      | PolicyLife
      | PolicySaving
      | PolicyCollectiveLife
      | (PolicyLife | PolicySaving | PolicyCollectiveLife)[]
  ) => {
    return iterate(p, policy => ({
      ...policy,
      isLifetime: diffGreaterOrEqualThan(
        99,
        policy.effectDate,
        policy.expirationDate
      )
    }));
  }
);

export function AutoUnsub(
  target: any,
  propertyKey: string,
  descriptor: PropertyDescriptor
) {
  const methodUnpatched = descriptor.value;
  descriptor.value = function(...args: any[]) {
    for (const propName in this) {
      if (this.hasOwnProperty(propName)) {
        const property = this[propName] || {};
        if (typeof property.unsubscribe === 'function') {
          property.unsubscribe();
        }
      }
    }

    return methodUnpatched.apply(this, args);
  };
}

export function arrayToObjectProps(array: any[], value = true) {
  return array.reduce((acc, key) => ({ [key]: value, ...acc }), {});
}

export function createHttpParams(
  params: {},
  httpParams = new HttpParams()
): HttpParams {
  Object.keys(params).forEach(param => {
    if (params[param]) {
      httpParams = httpParams.set(param, params[param]);
    }
  });

  return httpParams;
}

export const createObjectOfPoliciesById = (policies: PolicyBase[]) =>
  Object.assign({}, ...policies.map(p => ({ [p.policyNumber]: p }))) as {
    [key: string]: PolicyBase;
  };

export const joinStrings = (texts: string[], separator = '') => {
  if (!texts?.length) {
    return '';
  }
  return texts.join(separator);
}

export const getPhoneNumberAsLink = (phoneNumber = '', cssClass = 'ws-nw') => {
  return `<a class=\"${cssClass}\" href='tel:${phoneNumber}'>${phoneNumber}</a>`;
}

export function obfuscateEmail(str: string) {
  return str
    .split('@')
    .map((v, i) => (i ? v : v.substring(0, 3).concat('*****')))
    .join('@');
}

export const obfuscatePhoneCustomChar = (phone: string, character: string) =>
  `${character.repeat(6)}${phone.slice(6, 9)}`;

export const flat = <T>(array: any[]) => [].concat.apply([], array) as T[];

export const filterUniqueBasicTypesInList = (array: Array<string | number | boolean>) => {
  if (!array) { return []; }
  return array.filter((x: any, i: number, arr: any[] ) => arr.indexOf(x) === i);
};

export const isServeMode = () =>
  isDevMode() &&
  !location.href.includes('cloudfront') &&
  !location.href.includes('wmapfre.pre.mapfre.net');

export const findObjectFromArray = <T>(
  items: T[],
  item: any,
  propertyId = 'id'
) => {
  return items.find(i => i[propertyId] === item[propertyId]);
};

export const getObjectFromArray = <T>(
  items: T[],
  item: any,
  propertyId = 'id'
) => {
  const index = items.findIndex(i => i[propertyId] === item[propertyId]);
  return items[index] || {};
};

export const replaceOrPush = <T>(items: T[], item: any, propertyId = 'id') => {
  const index = items.findIndex(i => i[propertyId] === item[propertyId]);
  return replaceOrPushObject(items, item, index);
};

export const mergeOrPush = <T>(items: T[], item: any, propertyId = 'id') => {
  const index = items.findIndex(i => i[propertyId] === item[propertyId]);
  return mergeOrPushObject(items, item, index);
};

const replaceOrPushObject = <T>(items: T[], item: any, index: number) => {
  const res = [...items];
  if (index === -1) {
    res.push(item);
  } else {
    res[index] = item;
  }

  return res;
};

const mergeOrPushObject = <T>(items: T[], item: any, index: number) => {
  const res = [...items];

  index = index !== -1 ? index : res.length;
  res[index] = {
    ...res[index],
    ...item
  };

  return res;
};

export const extractNumbersFromText = (text: string) =>
  text.match(/(\d+)/g).reduce((acc, val) => [...acc, Number(val)], []);

export const isInTheFuture = (date: string | moment.Moment) => {
  const isTodayGreaterThan = isGreaterThan(date);
  return isTodayGreaterThan(moment());
};

export const isGreaterThan =
  (date: moment.Moment | string) => (thisDate: moment.Moment) =>
    thisDate.diff(date) < 0;

export const createMoment = (year: number) => (month: number, day: number) =>
  moment(new Date(year, month, day));

// YYYY-MM-DD
export const getDate = (): string => new Date().toISOString().substring(0, 10);

export const getFormattedDate = (date: Date, format: string): string =>
  moment(date).format(format);

export const getDaysInMonth = (date?: Date) =>
  moment(date || new Date()).daysInMonth();

export const equalObjects = <T>(arr1: T[], arr2: T[]) =>
  JSON.stringify(arr1) === JSON.stringify(arr2);

export const promisify =
  func =>
  (...args) =>
    new Promise((resolve, reject) =>
      func(
        ...args,
        result => resolve(result),
        err => reject(err)
      )
    );

export const enumToArray = enumm =>
  Object.keys(enumm)
    .filter(key => isNaN(Number(enumm[key])))
    .map(key => enumm[key]);

export const findOne = <T>(arr1: T[], arr2: T[]) =>
  arr1.some(v => arr2.includes(v));

export const getKeyFrom = (obj, value) =>
  Object.keys(obj).find(key => obj[key] === value);

export const hasInvestmentAndPensionPolicies = ({
  investmentNovaPolicies,
  pensionPolicies
}) => Boolean(investmentNovaPolicies.length || pensionPolicies.length);

export const toFalse = () => of(false);

export const pipe =
  (...fns) =>
  x =>
    fns.reduce((v, f) => f(v), x);

export const and = (a: boolean) => (b: boolean) => a && b;
export const no =
  <T>(g: (_: T) => boolean) =>
  (x: T) =>
    !g(x);

export const getEmissionDateOfLastYearCertificate = () => {
  const createMomentWithCurrentYear = createMoment(moment().year());
  return createMomentWithCurrentYear(3, 1);
};

export const sum = (acc, val) => acc + val;

export const insideOfWorkingHours = (date: moment.Moment) =>
  insideDayHourRange({
    date,
    startDay: WorkingDaysAndHours.STARTING_DAY,
    lastDay: WorkingDaysAndHours.ENDING_DAY,
    startingHour: WorkingDaysAndHours.STARTING_HOUR,
    endingHour: WorkingDaysAndHours.ENDING_HOUR
  });

export const insideOnCallShift = (date: moment.Moment) =>
  !insideDayHourRange({
    date,
    startDay: 1,
    lastDay: 5,
    startingHour: 8,
    endingHour: 19
  });

const insideDayHourRange = ({
  date,
  startDay,
  lastDay,
  startingHour,
  endingHour
}: DayHourRange) => {
  const dayDate = date.day();
  const hourDate = date.hour();

  return (
    dayDate >= startDay &&
    dayDate <= lastDay &&
    hourDate >= startingHour &&
    hourDate < endingHour
  );
};

export const stringifyList = (el, value, i, array) => {
  let concatLetter: string;
  if (i === 0) {
    concatLetter = '';
  } else if (i < array.length - 1) {
    concatLetter = ', ';
  } else {
    concatLetter = ' y ';
  }

  return el + concatLetter + value;
};
export interface ScheduleInput {
  from: {
    hour: number;
    minute?: number;
  };
  to: {
    hour: number;
    minute?: number;
  };
}
export interface ScheduleDateTime {
  from: string;
  to: string;
}

export const toScheduleDateTime = ({
  from,
  to
}: ScheduleInput): ScheduleDateTime => ({
  from: moment({ hour: from.hour, minute: from.minute }).toISOString(true),
  to: moment({ hour: to.hour, minute: to.minute }).toISOString(true)
});

export const isInFuture = ({ from }: ScheduleInput) => {
  const now = new Date();
  const currentHour = now.getHours() + 1;
  return from.hour >= currentHour;
};

export const isWeekend = (date: Date) =>
  date.getDay() === 0 || date.getDay() === 6;

export const isAndroidApp = (platform: Platform) =>
  platform.is('android') && platform.is('hybrid');

export function isNumber($event: any) {
  const charCode = $event.which ? $event.which : $event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    return false;
  }
  return true;
}

export function capitalize(str: string): string {
  if (str) {
    str = str.toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
  } else {
    return '';
  }
}

export function isValidPlateCar(plate: string) {
  const regex = new RegExp(/^\b[A-Z]{1,2}[0-9]{4}[A-Z]{1,2}\b$|^\b[0-9]{4}[A-Z]{3}\b$/);
  return regex.test(plate);
}

export function isValidPlateMotorBike(plate: string) {
  const regex = new RegExp(/^\b[A-Z]{1,2}[0-9]{4}[A-Z]{1,2}\b$|^\b[0-9]{4}[A-Z]{3}\b$|^\b[C-C]{1}[0-9]{4}[B-Z]{3}\b$/);
  return regex.test(plate);
}

export function isValidFrameNumber(frameNumber: string) {
  const regex = new RegExp(/^\b[A-Za-z0-9]{17}\b$/);
  return regex.test(frameNumber);
}

export function mergeArraysWithoutDuplicates(...args: Array<Array<number | string | boolean>>): Array<number | string | boolean> {
  let arr = [];
  args.forEach(a => {
    arr = [...arr, ...a];
  })
  return Array.from(new Set(arr));
}

export function stopVideos() {
  const videos = document.querySelectorAll('iframe');
  Array.prototype.forEach.call(videos, (video) => {
    const src = video.src;
    video.src = src;
  });
}

export function sanitazeDocumentNumber (documentNumber: string){
  let documentSanitize = documentNumber;
  if(documentNumber.length > 9) documentSanitize = documentNumber[0] === '0' ? documentNumber.slice(1) : documentNumber;
  return isValidPartialNif(documentSanitize) || isValidPartialNie(documentSanitize) ? documentSanitize : documentNumber;
}

export function getDocumentNumberType(documentNumber: string){
  const documentSanitize = sanitazeDocumentNumber(documentNumber);

  if(isValidPartialNif(documentSanitize)) return '0';
  if(isValidPartialNie(documentSanitize)) return '2';
}

export function isNotSpaceKeyCode(keyCode: number){
  return (keyCode !== 32);
}

export function hasSpecialCharacters(str): boolean{
  return str.match(/[&\/\\#,+()$~%.'":*?<>{}ºª]/g, '');
}

export function jsonClone(json) {
  return JSON.parse(JSON.stringify(json));
}

export function formatZipCode(zipCode) {
  if (!zipCode) {
    return null;
  }

  const code = zipCode.toString();

  return code.length < 5
    ? '0' + code
    : code;
}

export function getQueryStringParams(query: string) {
  return query
    ? (/^[?#]/.test(query) ? query.slice(1) : query)
      .split('&')
      .reduce((params, param) => {
          const [key, value] = param.split('=');
          params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
          return params;
      }, {})
    : {};
}
